// store/index.ts
import { configureStore } from '@reduxjs/toolkit'
import { setGlobalLogoutCallback } from '../services/api/axiosConfig'
import { logout } from './actions/auth.action'
import { xrayPanelPersistenceMiddleware } from './middleware/xrayPanelPersistence.middleware'
import {
  carrierReducer,
  templateReducer,
  taskReducer,
  listingReducer,
  managementReducer,
  claimReducer,
  claimsReducer,
  planReducer,
  benefitReducer,
  policyReducer,
  noteReducer,
  audioReducer,
  providerReducer,
  patientReducer,
  appointmentReducer,
  locationReducer,
  authReducer,
  operatoriesReducer,
  operatorySessionReducer,
  patientAPIReducer,
  procedureReducer,
  contactsReducer,
  callerReducer,
  callsReducer,
  faxReducer,
  emailReducer,
  uiReducer,
  claimProcedureReducer,
  claimAttachmentReducer,
  claimFeatureReducer,
  appointmentTypeReducer,
  procedureSettingsReducer,
  insuranceReducer,
  billingReducer,
  adjustmentTypeReducer,
  adjustmentReducer,
  discountReducer,
  membershipReducer,
  labReducer,
  treatmentPlanReducer,
  chatReducer,
  marketingReducer,
  folderReducer,
  contactGroupReducer,
  chartingReducer,
  blockedUserReducer,
  clinicReducer,
  messageCenterReducer,
  chatInternalReducer,
  formsReducer,
  reportsReducer,
  settingsCommunicationReducer,
  messagingSettingsReducer,
  notificationReducer,
  patientProfileReducer,
  allergyMedicalConditionReducer,
  reportsTemplateReducer,
} from './reducers'

export const store = configureStore({
  reducer: {
    template: templateReducer,
    carrier: carrierReducer,
    task: taskReducer,
    listing: listingReducer,
    management: managementReducer,
    claim: claimReducer,
    claims: claimsReducer,
    plan: planReducer,
    benefit: benefitReducer,
    policy: policyReducer,
    note: noteReducer,
    audio: audioReducer,
    provider: providerReducer,
    patient: patientReducer,
    appointment: appointmentReducer,
    location: locationReducer,
    auth: authReducer,
    operatory: operatoriesReducer,
    operatorySession: operatorySessionReducer,
    patientAPI: patientAPIReducer,
    procedure: procedureReducer,
    contacts: contactsReducer,
    caller: callerReducer,
    calls: callsReducer,
    fax: faxReducer,
    email: emailReducer,
    ui: uiReducer,
    claimProcedure: claimProcedureReducer,
    claimAttachment: claimAttachmentReducer,
    claimFeature: claimFeatureReducer,
    appointmentType: appointmentTypeReducer,
    procedureSettings: procedureSettingsReducer,
    insurance: insuranceReducer,
    billing: billingReducer,
    adjustmentType: adjustmentTypeReducer,
    adjustment: adjustmentReducer,
    discount: discountReducer,
    membership: membershipReducer,
    lab: labReducer,
    treatmentPlan: treatmentPlanReducer,
    chat: chatReducer,
    marketing: marketingReducer,
    folder: folderReducer,
    contactGroup: contactGroupReducer,
    charting: chartingReducer,
    blockedUser: blockedUserReducer,
    clinic: clinicReducer,
    messageCenter: messageCenterReducer,
    chatInternal: chatInternalReducer,
    forms: formsReducer,
    reports: reportsReducer,
    settingsCommunication: settingsCommunicationReducer,
    messagingSettings: messagingSettingsReducer,
    notification: notificationReducer,
    patientProfile: patientProfileReducer,
    allergyMedicalCondition: allergyMedicalConditionReducer,
    reportsTemplate: reportsTemplateReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Twilio Voice Device/Call instances are intentionally non-serializable
      serializableCheck: {
        ignoredActions: ['calls/setDevice', 'calls/setCurrentCall'],
        ignoredPaths: ['calls.device', 'calls.currentCall'],
      },
    }).prepend(xrayPanelPersistenceMiddleware.middleware),
})

// Set up the global logout callback for axios interceptor
setGlobalLogoutCallback((expiredRefreshToken: boolean) => {
  store.dispatch(logout(expiredRefreshToken))
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
